import type { CopyTable } from './copyParity';

export const SCREEN_COPY: CopyTable = {
  en: {
    'page.readyCta': 'Ready to see how this fits your team?',
    'page.newsFeedError': 'Live feed is temporarily unavailable. Showing fallback official sources.',
    'page.newsAria': 'Compliance news links',
    'page.releaseAria': 'Recent release updates',
    'architecture.heroTitle': 'Find the insights, experts, and tools to fuel your AI success.',
    'architecture.domain': 'Architecture domain',
    'architecture.references': 'Reference sources',
    'architecture.openReference': 'Open reference',
    'architecture.graphicAria': 'Graphical architecture cards',
    'platform.bullet1': 'Unified gateway access to screening and monitoring services',
    'platform.bullet2': 'Modular services enabled by configuration and deployment context',
    'platform.bullet3': 'Consistent control surfaces for audit-ready operational workflows',
    'platform.bullet4': 'One interface for risk visibility, alerts, and evidence continuity',
    'platform.detail1':
      'The platform route provides a practical overview of how teams orchestrate compliance workflows across modular services without fragmented tooling.',
    'platform.detail2':
      'It is designed for phased rollout, so teams can start with core controls and expand capabilities as operational maturity increases.',
    'blog.title': 'My blog',
    'blog.subtitle': 'Latest compliance and regulatory perspectives for operational teams.',
    'blog.readMore': 'Read more',
    'blog.aria': 'Blog posts',
    'customers.eyebrow': 'Customer Stories',
    'customers.title': 'How teams scale compliance operations with NextCompli.',
    'customers.subtitle':
      'Each route below turns a menu entry into a practical operating pattern with concrete rollout guidance, not a placeholder card.',
    'customers.featured': 'Featured Story',
    'customers.featuredAria': 'Featured customer story',
    'customers.attribution': 'Representative operating pattern for {industry} teams',
    'customers.readPlaybook': 'Read the featured playbook',
    'customers.outcome': 'Outcome',
    'customers.moreAria': 'More customer outcomes',
    'customers.cta': 'Want to build a similar compliance operating model?',
    'customers.talkTeam': 'Talk to our team',
    'customers.storyEyebrow': 'Customer Story',
    'customers.challenge': 'Challenge',
    'customers.pattern':
      'This page captures a representative operating pattern that NextCompli can support for {industry} teams.',
    'customers.implementation': 'Implementation move',
    'customers.guidance': 'Applicable guidance',
    'customers.guidanceBody':
      'The strongest customer-story pages are tied to practical operating guidance instead of generic testimonials. These references keep each route grounded in real controls and review patterns.',
    'customers.needPattern': 'Need this operating pattern in your own environment?',
    'customers.walkthrough': 'Request a walkthrough',
    'legal.hubIntro':
      'This legal hub centralizes core policy routes used by customers, security researchers, and procurement teams.',
    'legal.hubReview':
      'Policy language is reviewed periodically so route-level links remain aligned with product behavior, privacy rights workflows, and disclosure obligations.',
    'plans.title': 'NextCompli Products and Pricing',
    'plans.mostPopular': 'Most popular',
    'plans.tabsAria': 'Plan category tabs',
    'plans.cardsAria': 'Pricing plan cards',
    'dashboards.title': 'Explore Dashboards',
    'dashboards.subtitle':
      'Business value of compliance dashboards for continuous operational visibility and faster decisions.',
    'dashboards.value': 'Value proposition',
    'dashboards.p1':
      'Dashboards create one operating view for compliance posture, source freshness, and risk indicators so teams can respond faster with shared context.',
    'dashboards.p2':
      'Instead of fragmented reporting across tools, this model aligns legal, compliance, and engineering teams around the same measurable signal set and escalation pathway.',
    'dashboards.p3':
      'The result is faster triage, stronger executive reporting quality, and defensible audit readiness backed by repeatable monitoring evidence.',
    'dashboards.b1': 'Shortens time to detect and prioritize material compliance issues.',
    'dashboards.b2': 'Improves reporting quality with evidence-backed trendlines and performance snapshots.',
    'dashboards.b3': 'Aligns operations, legal, and product stakeholders on one risk narrative.',
    'dashboards.cta': 'Request platform walkthrough',
    'dashboards.card1': 'Unified compliance visibility',
    'dashboards.card2': 'Faster incident triage workflows',
    'dashboards.card3': 'Evidence-ready reporting outputs',
    'dashboards.card4': 'Executive risk trendline oversight',
    'liability.title': 'Liability Monitoring',
    'liability.subtitle': 'Business value of continuous liability monitoring across local files and public websites.',
    'liability.p1':
      'Continuous liability monitoring helps teams detect policy drift, unsupported claims, and outdated legal language before those issues create contractual, regulatory, or reputational exposure.',
    'liability.p2':
      'Based on compliance operations guidance and external control frameworks, the strongest pattern is to monitor both internal artifacts (local files, draft content, evidence bundles) and live public routes with the same control logic.',
    'liability.p3':
      'This dual-surface model supports defensible outcomes: control findings are traceable, remediation is measurable, and repeated scans provide a clear record of diligence.',
    'liability.cta': 'Request workflow walkthrough',
    'dod.title': 'Government Compliance',
    'dod.subtitle':
      'Business value of partnering with NextCompli to target, pursue, and execute DoD contracts with compliance discipline.',
    'dod.p1':
      'NextCompli helps teams convert fragmented federal opportunity work into a repeatable pipeline: opportunity targeting, eligibility checks, proposal readiness, and post-award compliance evidence management.',
    'dod.p2':
      'Internal planning artifacts and public federal acquisition guidance align on the same pattern: contract win-rate improves when organizations operationalize qualification, document control, and regulatory evidence from day one.',
    'dod.p3':
      'For teams new to DoD procurement, this reduces wasted bid effort, shortens response cycles, and lowers risk from missed compliance obligations across FAR/DFARS, cybersecurity, and reporting requirements.',
    'dod.card1': 'Targets higher-fit opportunities with structured qualification criteria.',
    'dod.card2': 'Improves proposal quality through documented compliance controls.',
    'disclosure.title': 'Responsible Disclosure',
    'disclosure.subtitle': 'Report suspected security vulnerabilities responsibly so they can be reviewed and remediated.',
    'disclosure.h1': 'Good-faith reporting expectations',
    'disclosure.b1': 'Do not publicly disclose vulnerabilities before coordinated remediation and validation.',
    'disclosure.b2': 'Do not exfiltrate data, disrupt service, or perform destructive testing.',
    'disclosure.b3': 'Include clear reproduction steps, route/component scope, and observed impact.',
    'disclosure.h2': 'Coordination lifecycle',
    'disclosure.p1':
      'We triage reports, validate findings, prioritize remediation, and communicate updates through designated support channels.',
    'disclosure.p2':
      'Where feasible, we align with coordinated disclosure timelines and publish remediation guidance after fixes are available.',
    'disclosure.h3': 'Reference resources',
    'trust.title': 'Trust',
    'trust.subtitle': 'Security, privacy, and operational assurance practices that support dependable customer operations.',
    'trust.h1': 'Security governance',
    'trust.p1':
      'NextCompli applies risk-based controls across infrastructure, application security, and operational workflows to support confidentiality, integrity, and availability objectives.',
    'trust.h2': 'Control operations',
    'trust.b1': 'Continuous monitoring and alerting for production-relevant control signals.',
    'trust.b2': 'Role-based access controls with operational auditability.',
    'trust.b3': 'Documented incident response and remediation workflows.',
    'trust.h3': 'Framework references',
    'trust.h4': 'Campus and higher education',
    'trust.p2':
      'Higher-education buyers can download NextCompli’s campus assurance pack and the official EDUCAUSE HECVAT 4 toolkit. We do not embed EDUCAUSE questionnaire text.',
    'trust.hecvat': 'EDUCAUSE HECVAT 4 toolkit',
    'trust.pack': 'NextCompli campus assurance pack',
    'trust.b4': 'Restricted-party screening for scholars, partners, and shipments.',
    'trust.b5': 'Research-export and CUI evidence via the DoD / CMMC console when a grant requires it.',
    'dmca.title': 'DMCA Notice Process',
    'dmca.subtitle': 'Submit copyright notifications and counter-notifications through NextCompli legal operations channels.',
    'dmca.p1':
      'DMCA notices should identify the copyrighted work, the allegedly infringing material, and contact details for the reporting party.',
    'dmca.p2': 'Counter-notifications must include the required legal attestations and sufficient identifying information.',
    'dmca.p3':
      'NextCompli reviews notices through legal operations workflows and may take action consistent with applicable law and platform policy.',
    'a11y.title': 'Accessibility Statement',
    'a11y.subtitle':
      'NextCompli is committed to improving accessibility and aligning public web experiences with WCAG-oriented practices.',
    'a11y.p1':
      'We continuously review accessibility barriers, prioritize remediation, and monitor critical defects through engineering workflows.',
    'a11y.p2':
      'If you encounter accessibility issues, contact support with route details, assistive technology context, and issue description.',
    'a11y.p3': 'Accessibility feedback is routed to remediation owners and tracked in the product quality process.',
    'a11y.audit': 'Latest audit report:',
    'a11y.remediation': 'Remediation completion:',
    'a11y.blockers': 'Open blockers:',
    'a11y.critical': 'Critical',
    'a11y.serious': 'Serious',
    'claims.title': 'Claim Evidence Registry',
    'claims.subtitle': 'Substantiation links for active public marketing claims.',
    'claims.evidence': 'Evidence:',
    'claims.reviewed': 'Last reviewed:',
    'claims.valid': 'Evidence URL valid:',
    'claims.yes': 'yes',
    'claims.no': 'no',
    'data.title': 'Your Data Choices',
    'data.subtitle': 'Access available privacy rights workflows for access, correction, deletion, and opt-out requests.',
    'data.intro':
      'Rights processing is designed to support major privacy frameworks, including rights to know, correct, delete, portability/export, and opt-out where applicable by jurisdiction.',
    'data.sla': 'DSAR SLA dashboard',
    'data.open': 'Open requests:',
    'data.review': 'In review:',
    'data.completed': 'Completed:',
    'data.overdue': 'Overdue:',
    'data.lookup': 'Request status lookup',
    'data.lookupPlaceholder': 'Enter request ID',
    'data.lookupResult': 'Lookup result:',
    'data.notFound': 'not found',
    'data.submitAnother': 'Submit another request',
    'data.responseTarget':
      'We target an initial response within 45 days, subject to legal verification and applicable jurisdictional requirements.',
    'cookie.title': 'Cookie Preferences',
    'cookie.subtitle':
      'Control non-essential cookies with clear, equal-choice options. Essential cookies stay on for security and core site reliability.',
    'cookie.version': 'Preference version:',
    'cookie.source': 'Current source:',
    'cookie.statusDefault': 'Non-essential cookies are off by default until you opt in.',
    'cookie.statusLoaded': 'Saved cookie preferences loaded for this browser.',
    'cookie.statusGpc': 'Global Privacy Control is detected; non-essential cookies remain disabled by default.',
    'cookie.statusAcceptAll': 'All cookie categories (including non-essential) are enabled for this browser.',
    'cookie.statusReject': 'Only essential cookies are enabled. Non-essential cookies are disabled.',
    'cookie.statusSaved': 'Your selected cookie preferences were saved.',
    'cookie.categories': 'Cookie categories',
    'cookie.essential': 'Essential cookies',
    'cookie.essentialBody':
      'Always on. Required for security controls, authentication continuity, and route/session reliability.',
    'cookie.alwaysActive': 'Always active',
    'cookie.functional': 'Functional cookies',
    'cookie.functionalBody': 'Store non-essential experience settings like language or interface preferences.',
    'cookie.analytics': 'Analytics cookies',
    'cookie.analyticsBody': 'Measure site performance and feature usage trends in aggregate to improve user experience.',
    'cookie.advertising': 'Advertising cookies',
    'cookie.advertisingBody': 'Support campaign attribution and related marketing measurement where applicable.',
    'cookie.on': 'On',
    'cookie.off': 'Off',
    'cookie.reject': 'Reject non-essential',
    'cookie.acceptAll': 'Accept all',
    'cookie.save': 'Save selection',
    'cookie.snapshot': 'Cookie transparency snapshot',
    'cookie.colCookie': 'Cookie',
    'cookie.colCategory': 'Category',
    'cookie.colPurpose': 'Purpose',
    'cookie.colRetention': 'Retention',
    'cookie.sessionPurpose': 'Maintains authenticated session continuity and request integrity.',
    'cookie.langPurpose': 'Remembers selected language for returning visits.',
    'cookie.analyticsPurpose': 'Aggregates anonymous usage to improve product guidance and site reliability.',
    'cookie.session': 'Session',
    'cookie.sixMonths': '6 months',
    'cookie.thirteenMonths': '13 months',
    'contact.help': 'How can we help?',
    'contact.call': 'Call Toll-Free',
    'contact.chat': 'Chat Now',
    'contact.askExpert': 'Ask an expert',
    'contact.emailUs': 'Email Us',
    'contact.troubleshootTitle': 'Troubleshooting chat',
    'contact.troubleshootIntro':
      'We’ll try to resolve your issue first. Support escalation is available only if this does not solve it.',
    'contact.you': 'You:',
    'contact.assistant': 'Assistant:',
    'contact.analyzing': 'Analyzing your issue…',
    'contact.solved': 'Did this solve your issue?',
    'contact.loginIssue': 'I cannot sign in',
    'contact.loginReply':
      'Try resetting your password first, then clear browser cache/cookies for this site and retry in a private window. If SSO is enabled, confirm your IdP session is active and your account is assigned to the application.',
    'contact.alertsIssue': 'Alerts are missing or delayed',
    'contact.alertsReply':
      'Verify monitoring is enabled for the target workflow, check source connectivity health, and confirm alert routing (email/webhook) is active. Delays usually come from paused schedules or destination delivery restrictions.',
    'contact.dataIssue': 'Data appears outdated',
    'contact.dataReply':
      'Run a manual refresh from the dashboard, then review source freshness timestamps and connector status. If one source is stale, reconnect credentials and re-run a scoped sync before full monitoring resumes.',
    'contact.apiIssue': 'Integration/API issue',
    'contact.apiReply':
      'Check API base URL, token scope, and request headers first. For webhook issues, validate endpoint SSL, response status, and retry logs. Most failures come from expired tokens or mismatched environment configuration.',
    'subscribe.checking': 'Checking session…',
    'subscribe.kicker': 'You’re subscribing to',
    'subscribe.contactEstimate': 'Contact for estimate',
    'subscribe.changePlan': 'Change plan',
    'subscribe.continueAs': 'Continue as {email}',
    'subscribe.signedInLead':
      'You’re signed in. Confirm the account name and continue. No card is required.',
    'subscribe.emailRequired': 'Email and password are required to create an account.',
    'subscribe.passwordLength': 'Password must be at least 8 characters.',
    'subscribe.unableStart': 'Unable to start subscription.',
    'subscribe.opening': 'Opening the console...',
    'subscribe.signInRequired': 'Email and password are required to sign in.',
    'subscribe.addEmail': 'Add the email for this subscription.',
    'subscribe.createAccount': 'Create your NextCompli account',
    'subscribe.signInContinue': 'Sign in to continue',
    'subscribe.createLead':
      'Name the account and set your password. You become the Admin. No card is required to open the account.',
    'subscribe.googleContinue': 'Continue with Google',
    'subscribe.googleConfirmed':
      'Google confirmed {email}. Add the account name and continue. You sign in with Google after this. There is no separate password, and no card is required.',
    'subscribe.googleNoAccount':
      'Google confirmed that address, but it is not a NextCompli user yet. Finish the form below.',
    'subscribe.googleProofExpired':
      'That Google confirmation expired. Choose Continue with Google again, then finish the form.',
    'subscribe.signinLead': 'Use your existing NextCompli credentials, then continue.',
    'subscribe.sso': 'Continue with company SSO',
    'subscribe.fullName': 'Full name',
    'subscribe.company': 'Company name (optional)',
    'subscribe.companyHint':
      'Optional. Invoices and the console use this name, and you can add or change it later. If you leave it blank, the account uses your name. If you were invited to a company that already pays, open that invite. Do not start a second subscription.',
    'subscribe.companyRequired': 'Enter the company name. That name is the subscription account.',
    'subscribe.cancel': 'Cancel',
    'subscribe.includedLicenses':
      '{count} people included. Each person is one license, and a pending invite counts. A larger team needs the next plan.',
    'subscribe.unlimitedLicenses':
      'Unlimited people on this company. Each person is still one named login.',
    'subscribe.password': 'Password',
    'subscribe.noCardYet': 'No card is required to open this account.',
    'subscribe.renews': 'Renews monthly until you cancel',
    'subscribe.youAreAdmin': 'You will be the subscription admin',
    'subscribe.afterPay': 'The password you type here is the one you sign in with. Continue opens the console.',
    'demo.hateForms': 'Hate filling out forms?',
    'demo.contactSales': 'Contact sales support',
    'demo.chatNow': 'Chat now',
    'demo.title': "We'd like to know you better so we can serve you better",
    'demo.subtitle': 'A 30-minute walkthrough of OFAC screening, government homework, and website checks for your team. This is not a free product dashboard.',
    'demo.kicker': 'Schedule a demo',
    'demo.headline': 'See NextCompli on your work, not a canned tour',
    'demo.bullet1': 'How screening and proof stay on one account',
    'demo.bullet2': 'Which plan covers OFAC, CMMC Level 1 for FCI, and website checks',
    'demo.bullet3': 'Why teammates live on the paying subscription, not a separate access list',
    'demo.firstName': 'First name',
    'demo.lastName': 'Last name',
    'demo.email': 'Work email',
    'demo.phone': 'Phone number',
    'demo.company': 'Company name',
    'demo.employees': 'Company headcount',
    'demo.employeesSelect': 'Please select',
    'demo.headcount.1': '1–10',
    'demo.headcount.2': '11–50',
    'demo.headcount.3': '51–200',
    'demo.headcount.4': '201–1,000',
    'demo.headcount.5': '1,001+',
    'demo.schedule': 'Schedule demo',
    'demo.submitting': 'Submitting…',
    'demo.thanks': 'Thanks — your request is in.',
    'demo.thanksBody': 'We will follow up with a time that matches your headcount and the work you named.',
    'demo.another': 'Submit another request',
    'demo.privacyAgree': 'By submitting, I agree to NextCompli’s Privacy Policy.',
    'demo.submitError': 'We could not submit your request right now. Please retry or call sales support.',
    'sales.title': 'Sales assistant',
    'sales.subtitle': 'Ask product and pricing questions while sharing lead details for follow-up.',
    'sales.fullName': 'Full name',
    'sales.workEmail': 'Work email',
    'sales.role': 'Role',
    'sales.prompt': 'What are you trying to solve?',
    'sales.asking': 'Asking sales assistant…',
    'sales.ask': 'Ask sales assistant',
    'sales.leadCaptured': 'Lead details captured for sales follow-up.',
    'sales.provider': 'Provider:',
    'sales.unable': 'Unable to answer right now. Please try again.',
    'signup.ready': 'Ready to scale your business?',
    'signup.value':
      'Easily collaborate with your team with shared workflows, centralized login, and connected compliance operations.',
    'signup.planBtn': 'Start CompliCore',
    'signup.planLead': 'CompliCore is $99 a month. No card is required. You become the subscription admin. The company name can wait.',
    'signup.title': 'Create your account',
    'signup.company': 'Company (optional)',
    'signup.role': 'Job title (optional)',
    'signup.emailHint': 'Use your email and a password. Google and Microsoft are optional.',
    'signup.created': 'Account created. Sign in with this email and password.',
    'signup.creating': 'Creating account…',
    'signup.unable': 'The account could not be created. Try again.',
    'signup.google': 'Continue with Google',
    'signup.facebookDisabled': 'Facebook sign-in is not enabled. Use Google or Microsoft.',
    'reset.title': 'Choose a new password',
    'reset.subtitle':
      'Use at least 12 characters. Show the password to verify typing; paste from a password manager is allowed.',
    'reset.missingToken': 'Reset token is missing from the URL.',
    'reset.tooShort': 'Password must be at least 12 characters long.',
    'reset.mismatch': 'Passwords do not match.',
    'reset.unable': 'Unable to reset password.',
    'reset.updated': 'Password updated. Redirecting to sign in…',
    'reset.unreachable': 'Unable to reach authentication service. Please try again.',
    'reset.newPassword': 'New password',
    'invite.title': 'Accept Invite',
    'invite.subtitle': 'Set your password to activate your account invitation.',
    'invite.googleBody': 'This mailbox signs in with Google. Continue with Google to accept the invitation. NextCompli does not ask for a separate password.',
    'invite.microsoftBody': 'This mailbox signs in with Microsoft. Continue with Microsoft to accept the invitation. NextCompli does not ask for a separate password.',
    'invite.microsoftButton': 'Continue with Microsoft',
    'invite.googleSessionBody': 'You are already signed in as this mailbox. Accept the invitation here — no second Google sign-in is needed.',
    'invite.googleButton': 'Continue with Google',
    'invite.sessionButton': 'Accept invitation',
    'invite.otherGoogle': 'Use a different Google account',
    'invite.missingToken': 'Invite token is missing from the URL.',
    'invite.accepted': 'Invite accepted. Redirecting to login...',
    'invite.acceptedConsole': 'Invite accepted. Opening the console…',
    'invite.notFound':
      'This invite link is no longer valid. Please check your email for a more recent invite from NextCompli, or ask your administrator to resend the invitation.',
    'invite.inactive':
      'This invite link has already been used or was replaced by a newer one. If you need access, ask your administrator to resend the invitation.',
    'invite.unable': 'Unable to accept invite.',
    'invite.unreachable': 'Unable to reach invite service. Please try again.',
    'invite.placeholder': 'Enter a new password',
    'forgot.unable': 'Unable to start password reset.',
    'forgot.sent': 'If an account exists for that email, password reset instructions were sent.',
    'product.valueTitle': 'Value proposition',
    'terms.versionHistory': '9. Version history and replay proof',
    'terms.currentHash': 'Current version hash:',
    'terms.replay': 'Replay reference:',
    'cookie.bestPractice': 'Best-practice controls implemented',
    'cookie.equalChoice': 'Equal choice controls:',
    'cookie.equalChoiceBody': 'reject and accept options are presented with equal prominence.',
    'cookie.granular': 'Granular consent:',
    'cookie.granularBody': 'non-essential categories can be toggled independently.',
    'cookie.noPreEnabled': 'No pre-enabled non-essential cookies:',
    'cookie.noPreEnabledBody': 'functional, analytics, and advertising default to off until opted in.',
    'cookie.withdraw': 'Easy withdrawal:',
    'cookie.withdrawBody': 'you can revisit this page anytime to change and save choices.',
    'cookie.signal': 'Signal-aware defaults:',
    'cookie.signalBody': 'if Global Privacy Control is detected, non-essential cookies remain disabled by default.',
    'cookie.needRights': 'Need data-rights support in addition to cookie controls? Use',
    'cookie.orReview': 'or review',
    'cookie.privacyPolicy': 'Privacy Policy',
    'cookie.sessionRetention': 'Session',
    'cookie.analyticsTablePurpose': 'Supports aggregate usage measurement and performance tuning.',
    'cookie.adPurpose': 'Attributes marketing campaign interactions where enabled.',
    'cookie.ninetyDays': '90 days',
    'cookie.essentialCat': 'Essential',
    'cookie.functionalCat': 'Functional',
    'cookie.analyticsCat': 'Analytics',
    'cookie.advertisingCat': 'Advertising',
  },
  es: {
    'page.readyCta': '¿Listo para ver cómo encaja esto con tu equipo?',
    'page.newsFeedError': 'El feed en vivo no está disponible temporalmente. Mostramos fuentes oficiales de respaldo.',
    'page.newsAria': 'Enlaces de noticias de cumplimiento',
    'page.releaseAria': 'Actualizaciones recientes de versión',
    'architecture.heroTitle': 'Encuentra ideas, expertos y herramientas para impulsar tu éxito con IA.',
    'architecture.domain': 'Dominio de arquitectura',
    'architecture.references': 'Fuentes de referencia',
    'architecture.openReference': 'Abrir referencia',
    'architecture.graphicAria': 'Tarjetas gráficas de arquitectura',
    'platform.bullet1': 'Acceso unificado por gateway a servicios de evaluación y monitoreo',
    'platform.bullet2': 'Servicios modulares activados por configuración y contexto de despliegue',
    'platform.bullet3': 'Superficies de control consistentes para flujos operativos listos para auditoría',
    'platform.bullet4': 'Una interfaz para visibilidad de riesgo, alertas y continuidad de evidencia',
    'platform.detail1':
      'La ruta de plataforma ofrece una visión práctica de cómo los equipos orquestan flujos de cumplimiento en servicios modulares, sin herramientas fragmentadas.',
    'platform.detail2':
      'Está diseñada para un despliegue por fases, de modo que los equipos empiecen con controles esenciales y amplíen capacidades según madurez operativa.',
    'blog.title': 'Mi blog',
    'blog.subtitle': 'Perspectivas recientes de cumplimiento y regulación para equipos operativos.',
    'blog.readMore': 'Leer más',
    'blog.aria': 'Publicaciones del blog',
    'customers.eyebrow': 'Historias de clientes',
    'customers.title': 'Cómo los equipos escalan operaciones de cumplimiento con NextCompli.',
    'customers.subtitle':
      'Cada ruta convierte una entrada de menú en un patrón operativo práctico, con guía de implementación concreta, no una tarjeta vacía.',
    'customers.featured': 'Historia destacada',
    'customers.featuredAria': 'Historia de cliente destacada',
    'customers.attribution': 'Patrón operativo representativo para equipos de {industry}',
    'customers.readPlaybook': 'Leer el playbook destacado',
    'customers.outcome': 'Resultado',
    'customers.moreAria': 'Más resultados de clientes',
    'customers.cta': '¿Quieres construir un modelo operativo de cumplimiento similar?',
    'customers.talkTeam': 'Habla con nuestro equipo',
    'customers.storyEyebrow': 'Historia de cliente',
    'customers.challenge': 'Desafío',
    'customers.pattern':
      'Esta página captura un patrón operativo representativo que NextCompli puede apoyar para equipos de {industry}.',
    'customers.implementation': 'Paso de implementación',
    'customers.guidance': 'Guía aplicable',
    'customers.guidanceBody':
      'Las mejores páginas de clientes se vinculan a guía operativa práctica, no a testimonios genéricos. Estas referencias mantienen cada ruta anclada en controles y patrones de revisión reales.',
    'customers.needPattern': '¿Necesitas este patrón operativo en tu propio entorno?',
    'customers.walkthrough': 'Solicitar una guía',
    'legal.hubIntro':
      'Este centro legal concentra las rutas de políticas que usan clientes, investigadores de seguridad y equipos de compras.',
    'legal.hubReview':
      'El lenguaje de las políticas se revisa periódicamente para que los enlaces coincidan con el comportamiento del producto, los flujos de derechos de privacidad y las obligaciones de divulgación.',
    'plans.title': 'Productos y precios de NextCompli',
    'plans.mostPopular': 'Más popular',
    'plans.tabsAria': 'Pestañas de categoría de planes',
    'plans.cardsAria': 'Tarjetas de planes de precios',
    'dashboards.title': 'Explorar paneles',
    'dashboards.subtitle':
      'Valor de negocio de los paneles de cumplimiento para visibilidad operativa continua y decisiones más rápidas.',
    'dashboards.value': 'Propuesta de valor',
    'dashboards.p1':
      'Los paneles crean una vista operativa de postura de cumplimiento, frescura de fuentes e indicadores de riesgo para responder más rápido con contexto compartido.',
    'dashboards.p2':
      'En lugar de reportes fragmentados entre herramientas, este modelo alinea a legal, cumplimiento e ingeniería en el mismo conjunto de señales y ruta de escalamiento.',
    'dashboards.p3':
      'El resultado es un triage más rápido, mejor reporte ejecutivo y preparación de auditoría respaldada por evidencia de monitoreo repetible.',
    'dashboards.b1': 'Reduce el tiempo para detectar y priorizar problemas materiales de cumplimiento.',
    'dashboards.b2': 'Mejora la calidad del reporte con tendencias y capturas de desempeño respaldadas por evidencia.',
    'dashboards.b3': 'Alinea a operaciones, legal y producto en una misma narrativa de riesgo.',
    'dashboards.cta': 'Solicitar recorrido de plataforma',
    'dashboards.card1': 'Visibilidad unificada de cumplimiento',
    'dashboards.card2': 'Flujos más rápidos de triage de incidentes',
    'dashboards.card3': 'Salidas de reporte listas para evidencia',
    'dashboards.card4': 'Supervisión ejecutiva de tendencias de riesgo',
    'liability.title': 'Monitoreo de responsabilidad',
    'liability.subtitle':
      'Valor de negocio del monitoreo continuo de responsabilidad en archivos locales y sitios públicos.',
    'liability.p1':
      'El monitoreo continuo de responsabilidad ayuda a detectar deriva de políticas, afirmaciones no respaldadas y lenguaje legal desactualizado antes de que generen exposición contractual, regulatoria o reputacional.',
    'liability.p2':
      'Según la guía de operaciones de cumplimiento y marcos de control externos, el patrón más sólido es monitorear artefactos internos (archivos locales, borradores, paquetes de evidencia) y rutas públicas con la misma lógica de control.',
    'liability.p3':
      'Este modelo de doble superficie respalda resultados defendibles: los hallazgos son trazables, la remediación es medible y los escaneos repetidos dejan un registro claro de diligencia.',
    'liability.cta': 'Solicitar recorrido de flujo',
    'dod.title': 'Cumplimiento gubernamental',
    'dod.subtitle':
      'Valor de asociarse con NextCompli para identificar, perseguir y ejecutar trabajo de proveedores federales, estatales, de condado y de ciudad con disciplina de cumplimiento.',
    'dod.p1':
      'NextCompli ayuda a convertir el trabajo fragmentado de oportunidades federales en un pipeline repetible: segmentación, elegibilidad, preparación de propuestas y evidencia de cumplimiento posterior a la adjudicación.',
    'dod.p2':
      'Los artefactos internos de planificación y la guía pública de adquisiciones federales coinciden: la tasa de ganancia mejora cuando la organización opera calificación, control documental y evidencia regulatoria desde el primer día.',
    'dod.p3':
      'Para equipos nuevos en adquisiciones DoD, esto reduce esfuerzo de oferta desperdiciado, acorta ciclos de respuesta y baja el riesgo de obligaciones omitidas en FAR/DFARS, ciberseguridad y reportes.',
    'dod.card1': 'Apunta a oportunidades de mejor ajuste con criterios de calificación estructurados.',
    'dod.card2': 'Mejora la calidad de las propuestas con controles de cumplimiento documentados.',
    'disclosure.title': 'Divulgación responsable',
    'disclosure.subtitle':
      'Reporta vulnerabilidades de seguridad de forma responsable para que puedan revisarse y remediarse.',
    'disclosure.h1': 'Expectativas de reporte de buena fe',
    'disclosure.b1': 'No divulgues vulnerabilidades públicamente antes de la remediación y validación coordinadas.',
    'disclosure.b2': 'No extraigas datos, interrumpas el servicio ni realices pruebas destructivas.',
    'disclosure.b3': 'Incluye pasos claros de reproducción, alcance de ruta/componente e impacto observado.',
    'disclosure.h2': 'Ciclo de coordinación',
    'disclosure.p1':
      'Clasificamos reportes, validamos hallazgos, priorizamos remediación y comunicamos actualizaciones por canales de soporte designados.',
    'disclosure.p2':
      'Cuando es posible, alineamos plazos de divulgación coordinada y publicamos guía de remediación después de los correctivos.',
    'disclosure.h3': 'Recursos de referencia',
    'trust.title': 'Confianza',
    'trust.subtitle':
      'Prácticas de seguridad, privacidad y aseguramiento operativo que respaldan operaciones confiables para el cliente.',
    'trust.h1': 'Gobernanza de seguridad',
    'trust.p1':
      'NextCompli aplica controles basados en riesgo en infraestructura, seguridad de aplicaciones y flujos operativos para apoyar confidencialidad, integridad y disponibilidad.',
    'trust.h2': 'Operación de controles',
    'trust.b1': 'Monitoreo y alertas continuas para señales de control relevantes en producción.',
    'trust.b2': 'Controles de acceso basados en roles con auditabilidad operativa.',
    'trust.b3': 'Flujos documentados de respuesta a incidentes y remediación.',
    'trust.h3': 'Referencias de marcos',
    'trust.h4': 'Campus y educación superior',
    'trust.p2':
      'Los compradores de educación superior pueden descargar el paquete de aseguramiento de campus de NextCompli y el kit oficial HECVAT 4 de EDUCAUSE. No incorporamos el texto del cuestionario de EDUCAUSE.',
    'trust.hecvat': 'Kit HECVAT 4 de EDUCAUSE',
    'trust.pack': 'Paquete de aseguramiento de campus de NextCompli',
    'trust.b4': 'Evaluación de partes restringidas para académicos, socios y envíos.',
    'trust.b5': 'Evidencia de exportación de investigación y CUI mediante la consola DoD / CMMC cuando una subvención lo exige.',
    'dmca.title': 'Proceso de avisos DMCA',
    'dmca.subtitle':
      'Envía notificaciones de derechos de autor y contranotificaciones a través de los canales legales de NextCompli.',
    'dmca.p1':
      'Los avisos DMCA deben identificar la obra protegida, el material presuntamente infractor y los datos de contacto de quien reporta.',
    'dmca.p2':
      'Las contranotificaciones deben incluir las declaraciones legales requeridas e información de identificación suficiente.',
    'dmca.p3':
      'NextCompli revisa los avisos en flujos de operaciones legales y puede actuar de acuerdo con la ley aplicable y la política de la plataforma.',
    'a11y.title': 'Declaración de accesibilidad',
    'a11y.subtitle':
      'NextCompli se compromete a mejorar la accesibilidad y alinear las experiencias web públicas con prácticas orientadas a WCAG.',
    'a11y.p1':
      'Revisamos barreras de accesibilidad de forma continua, priorizamos remediación y monitoreamos defectos críticos en flujos de ingeniería.',
    'a11y.p2':
      'Si encuentras problemas de accesibilidad, contacta a soporte con la ruta, el contexto de tecnología de asistencia y la descripción del problema.',
    'a11y.p3':
      'Los comentarios de accesibilidad se asignan a responsables de remediación y se rastrean en el proceso de calidad del producto.',
    'a11y.audit': 'Último informe de auditoría:',
    'a11y.remediation': 'Finalización de remediación:',
    'a11y.blockers': 'Bloqueadores abiertos:',
    'a11y.critical': 'Críticos',
    'a11y.serious': 'Serios',
    'claims.title': 'Registro de evidencia de afirmaciones',
    'claims.subtitle': 'Enlaces de respaldo para afirmaciones de marketing públicas activas.',
    'claims.evidence': 'Evidencia:',
    'claims.reviewed': 'Última revisión:',
    'claims.valid': 'URL de evidencia válida:',
    'claims.yes': 'sí',
    'claims.no': 'no',
    'data.title': 'Tus opciones de datos',
    'data.subtitle':
      'Accede a flujos de derechos de privacidad para solicitudes de acceso, corrección, eliminación y exclusión.',
    'data.intro':
      'El procesamiento de derechos está diseñado para marcos de privacidad importantes, incluidos derechos a conocer, corregir, eliminar, portabilidad/exportación y exclusión cuando aplique por jurisdicción.',
    'data.sla': 'Panel de SLA de DSAR',
    'data.open': 'Solicitudes abiertas:',
    'data.review': 'En revisión:',
    'data.completed': 'Completadas:',
    'data.overdue': 'Vencidas:',
    'data.lookup': 'Consulta de estado de solicitud',
    'data.lookupPlaceholder': 'Ingresa el ID de solicitud',
    'data.lookupResult': 'Resultado de la consulta:',
    'data.notFound': 'no encontrado',
    'data.submitAnother': 'Enviar otra solicitud',
    'data.responseTarget':
      'Buscamos una respuesta inicial en 45 días, sujeta a verificación legal y requisitos jurisdiccionales aplicables.',
    'cookie.title': 'Preferencias de cookies',
    'cookie.subtitle':
      'Controla las cookies no esenciales con opciones claras e iguales. Las cookies esenciales permanecen activas por seguridad y fiabilidad del sitio.',
    'cookie.version': 'Versión de preferencia:',
    'cookie.source': 'Origen actual:',
    'cookie.statusDefault': 'Las cookies no esenciales están desactivadas por defecto hasta que las aceptes.',
    'cookie.statusLoaded': 'Se cargaron las preferencias de cookies guardadas en este navegador.',
    'cookie.statusGpc':
      'Se detectó Control de privacidad global; las cookies no esenciales permanecen desactivadas por defecto.',
    'cookie.statusAcceptAll': 'Todas las categorías de cookies (incluidas las no esenciales) están activadas en este navegador.',
    'cookie.statusReject': 'Solo las cookies esenciales están activadas. Las no esenciales están desactivadas.',
    'cookie.statusSaved': 'Tus preferencias de cookies seleccionadas se guardaron.',
    'cookie.categories': 'Categorías de cookies',
    'cookie.essential': 'Cookies esenciales',
    'cookie.essentialBody':
      'Siempre activas. Necesarias para controles de seguridad, continuidad de autenticación y fiabilidad de rutas/sesión.',
    'cookie.alwaysActive': 'Siempre activas',
    'cookie.functional': 'Cookies funcionales',
    'cookie.functionalBody': 'Guardan ajustes de experiencia no esenciales, como idioma o preferencias de interfaz.',
    'cookie.analytics': 'Cookies de analítica',
    'cookie.analyticsBody':
      'Miden el rendimiento del sitio y el uso de funciones de forma agregada para mejorar la experiencia.',
    'cookie.advertising': 'Cookies de publicidad',
    'cookie.advertisingBody': 'Apoyan la atribución de campañas y la medición de marketing cuando aplica.',
    'cookie.on': 'Activado',
    'cookie.off': 'Desactivado',
    'cookie.reject': 'Rechazar no esenciales',
    'cookie.acceptAll': 'Aceptar todas',
    'cookie.save': 'Guardar selección',
    'cookie.snapshot': 'Resumen de transparencia de cookies',
    'cookie.colCookie': 'Cookie',
    'cookie.colCategory': 'Categoría',
    'cookie.colPurpose': 'Propósito',
    'cookie.colRetention': 'Retención',
    'cookie.sessionPurpose': 'Mantiene la continuidad de la sesión autenticada y la integridad de las solicitudes.',
    'cookie.langPurpose': 'Recuerda el idioma seleccionado en visitas posteriores.',
    'cookie.analyticsPurpose': 'Agrega uso anónimo para mejorar la guía de producto y la fiabilidad del sitio.',
    'cookie.session': 'Sesión',
    'cookie.sixMonths': '6 meses',
    'cookie.thirteenMonths': '13 meses',
    'contact.help': '¿Cómo podemos ayudarte?',
    'contact.call': 'Llamar sin costo',
    'contact.chat': 'Chatear ahora',
    'contact.askExpert': 'Pregunta a un experto',
    'contact.emailUs': 'Escríbenos',
    'contact.troubleshootTitle': 'Chat de diagnóstico',
    'contact.troubleshootIntro':
      'Intentaremos resolver tu problema primero. La escalación a soporte solo está disponible si esto no lo soluciona.',
    'contact.you': 'Tú:',
    'contact.assistant': 'Asistente:',
    'contact.analyzing': 'Analizando tu problema…',
    'contact.solved': '¿Esto resolvió tu problema?',
    'contact.loginIssue': 'No puedo iniciar sesión',
    'contact.loginReply':
      'Prueba restablecer la contraseña, limpia caché/cookies de este sitio y reintenta en una ventana privada. Si SSO está activo, confirma que la sesión del IdP esté activa y que tu cuenta esté asignada a la aplicación.',
    'contact.alertsIssue': 'Faltan alertas o llegan con retraso',
    'contact.alertsReply':
      'Verifica que el monitoreo esté activo para el flujo, revisa la conectividad de fuentes y confirma el enrutamiento de alertas (correo/webhook). Los retrasos suelen venir de programaciones pausadas o restricciones de entrega.',
    'contact.dataIssue': 'Los datos parecen desactualizados',
    'contact.dataReply':
      'Ejecuta una actualización manual desde el panel, luego revisa marcas de frescura y estado del conector. Si una fuente está obsoleta, vuelve a conectar credenciales y ejecuta una sincronización acotada.',
    'contact.apiIssue': 'Problema de integración/API',
    'contact.apiReply':
      'Revisa primero la URL base de la API, el alcance del token y los encabezados. Para webhooks, valida SSL, estado de respuesta y reintentos. La mayoría de fallas vienen de tokens vencidos o entornos mal configurados.',
    'subscribe.checking': 'Comprobando sesión…',
    'subscribe.kicker': 'Te estás suscribiendo a',
    'subscribe.contactEstimate': 'Contactar para estimación',
    'subscribe.changePlan': 'Cambiar plan',
    'subscribe.continueAs': 'Continuar como {email}',
    'subscribe.signedInLead':
      'Ya iniciaste sesión. Confirma el nombre de la cuenta y continúa. No se necesita una tarjeta.',
    'subscribe.emailRequired': 'El correo y la contraseña son obligatorios para crear una cuenta.',
    'subscribe.passwordLength': 'La contraseña debe tener al menos 8 caracteres.',
    'subscribe.unableStart': 'No se pudo iniciar la suscripción.',
    'subscribe.opening': 'Abriendo la consola...',
    'subscribe.signInRequired': 'El correo y la contraseña son obligatorios para iniciar sesión.',
    'subscribe.addEmail': 'Añade el correo de esta suscripción.',
    'subscribe.createAccount': 'Crea tu cuenta de NextCompli',
    'subscribe.signInContinue': 'Inicia sesión para continuar',
    'subscribe.createLead':
      'Nombra la cuenta y elige una contraseña. Tú serás Admin. No se necesita una tarjeta para abrir la cuenta.',
    'subscribe.googleContinue': 'Continuar con Google',
    'subscribe.googleConfirmed':
      'Google confirmó {email}. Añade el nombre de la cuenta y continúa. Después entras con Google. No hay otra contraseña, y no se necesita una tarjeta.',
    'subscribe.googleNoAccount':
      'Google confirmó esa dirección, pero todavía no es un usuario de NextCompli. Termina el formulario de abajo.',
    'subscribe.googleProofExpired':
      'Esa confirmación de Google caducó. Elige Continuar con Google otra vez y termina el formulario.',
    'subscribe.signinLead': 'Usa tus credenciales de NextCompli y continúa.',
    'subscribe.sso': 'Continuar con SSO de la empresa',
    'subscribe.fullName': 'Nombre completo',
    'subscribe.company': 'Nombre de la empresa (opcional)',
    'subscribe.companyHint':
      'Opcional. Las facturas y la consola usan este nombre, y puedes añadirlo o cambiarlo después. Si lo dejas vacío, la cuenta usa tu nombre. Si te invitaron a una empresa que ya paga, abre esa invitación. No inicies una segunda suscripción.',
    'subscribe.companyRequired': 'Escribe el nombre de la empresa. Ese nombre es la cuenta de suscripción.',
    'subscribe.cancel': 'Cancelar',
    'subscribe.includedLicenses':
      '{count} personas incluidas. Cada persona es una licencia, y una invitación pendiente cuenta. Un equipo más grande necesita el siguiente plan.',
    'subscribe.unlimitedLicenses':
      'Personas ilimitadas en esta empresa. Cada persona sigue siendo un acceso con nombre.',
    'subscribe.password': 'Contraseña',
    'subscribe.noCardYet': 'No se necesita una tarjeta para abrir esta cuenta.',
    'subscribe.renews': 'Se renueva cada mes hasta que canceles',
    'subscribe.youAreAdmin': 'Serás admin de la suscripción',
    'subscribe.afterPay': 'La contraseña que escribes aquí es la que usas para entrar. Continuar abre la consola.',
    'demo.hateForms': '¿Odias llenar formularios?',
    'demo.contactSales': 'Contactar soporte de ventas',
    'demo.chatNow': 'Chatear ahora',
    'demo.title': 'Queremos conocerte mejor para servirte mejor',
    'demo.subtitle': 'Una sesión de 30 minutos sobre cribado OFAC, tarea gubernamental y revisiones del sitio. Esto no es un panel de producto gratis.',
    'demo.kicker': 'Agendar una demo',
    'demo.headline': 'Ve NextCompli en tu trabajo, no un recorrido genérico',
    'demo.bullet1': 'Cómo el cribado y la prueba quedan en una cuenta',
    'demo.bullet2': 'Qué plan cubre OFAC, CMMC Nivel 1 para FCI y revisiones del sitio',
    'demo.bullet3': 'Por qué el equipo vive en la suscripción de pago, no en un catálogo aparte',
    'demo.firstName': 'Nombre',
    'demo.lastName': 'Apellido',
    'demo.email': 'Correo laboral',
    'demo.phone': 'Teléfono',
    'demo.company': 'Empresa',
    'demo.employees': 'Plantilla de la empresa',
    'demo.employeesSelect': 'Seleccione',
    'demo.headcount.1': '1–10',
    'demo.headcount.2': '11–50',
    'demo.headcount.3': '51–200',
    'demo.headcount.4': '201–1,000',
    'demo.headcount.5': '1,001+',
    'demo.schedule': 'Agendar demo',
    'demo.submitting': 'Enviando…',
    'demo.thanks': 'Gracias — recibimos tu solicitud.',
    'demo.thanksBody': 'Te escribiremos con un horario según el tamaño de tu equipo y el trabajo que nombraste.',
    'demo.another': 'Enviar otra solicitud',
    'demo.privacyAgree': 'Al enviar, acepto la Política de privacidad de NextCompli.',
    'demo.submitError': 'No pudimos enviar tu solicitud ahora. Reintenta o llama a soporte de ventas.',
    'sales.title': 'Asistente de ventas',
    'sales.subtitle': 'Haz preguntas de producto y precios mientras compartes datos de contacto para seguimiento.',
    'sales.fullName': 'Nombre completo',
    'sales.workEmail': 'Correo de trabajo',
    'sales.role': 'Rol',
    'sales.prompt': '¿Qué estás tratando de resolver?',
    'sales.asking': 'Consultando al asistente de ventas…',
    'sales.ask': 'Preguntar al asistente de ventas',
    'sales.leadCaptured': 'Datos de contacto capturados para seguimiento de ventas.',
    'sales.provider': 'Proveedor:',
    'sales.unable': 'No se puede responder ahora. Inténtalo de nuevo.',
    'signup.ready': '¿Listo para escalar tu negocio?',
    'signup.value':
      'Colabora fácilmente con tu equipo mediante flujos compartidos, inicio de sesión centralizado y operaciones de cumplimiento conectadas.',
    'signup.planBtn': 'Empezar CompliCore',
    'signup.planLead': 'CompliCore cuesta $99 al mes. No se necesita una tarjeta. Tú serás admin de la suscripción. El nombre de la empresa puede esperar.',
    'signup.title': 'Crea tu cuenta',
    'signup.company': 'Empresa (opcional)',
    'signup.role': 'Puesto (opcional)',
    'signup.emailHint': 'Usa tu correo y una contraseña. Google y Microsoft son opcionales.',
    'signup.created': 'Cuenta creada. Entra con este correo y esta contraseña.',
    'signup.creating': 'Creando la cuenta…',
    'signup.unable': 'No se pudo crear la cuenta. Inténtalo de nuevo.',
    'signup.google': 'Continuar con Google',
    'signup.facebookDisabled': 'El inicio de sesión con Facebook no está habilitado. Usa Google o Microsoft.',
    'reset.title': 'Elige una nueva contraseña',
    'reset.subtitle':
      'Usa al menos 12 caracteres. Muestra la contraseña para verificar lo que escribes; puedes pegar desde un gestor de contraseñas.',
    'reset.missingToken': 'Falta el token de restablecimiento en la URL.',
    'reset.tooShort': 'La contraseña debe tener al menos 12 caracteres.',
    'reset.mismatch': 'Las contraseñas no coinciden.',
    'reset.unable': 'No se pudo restablecer la contraseña.',
    'reset.updated': 'Contraseña actualizada. Redirigiendo al inicio de sesión…',
    'reset.unreachable': 'No se pudo contactar el servicio de autenticación. Inténtalo de nuevo.',
    'reset.newPassword': 'Nueva contraseña',
    'invite.title': 'Aceptar invitación',
    'invite.subtitle': 'Establece tu contraseña para activar la invitación de tu cuenta.',
    'invite.googleBody': 'Este correo entra con Google. Continúe con Google para aceptar la invitación. NextCompli no pide otra contraseña.',
    'invite.microsoftBody': 'Este correo entra con Microsoft. Continúe con Microsoft para aceptar la invitación. NextCompli no pide otra contraseña.',
    'invite.microsoftButton': 'Continuar con Microsoft',
    'invite.googleSessionBody': 'Ya tiene sesión con este correo. Acepte la invitación aquí — no hace falta volver a entrar con Google.',
    'invite.googleButton': 'Continuar con Google',
    'invite.sessionButton': 'Aceptar invitación',
    'invite.otherGoogle': 'Usar otra cuenta de Google',
    'invite.missingToken': 'Falta el token de invitación en la URL.',
    'invite.accepted': 'Invitación aceptada. Redirigiendo al inicio de sesión...',
    'invite.acceptedConsole': 'Invitación aceptada. Abriendo la consola…',
    'invite.notFound':
      'Este enlace de invitación ya no es válido. Revisa tu correo por una invitación más reciente de NextCompli, o pide a tu administrador que la reenvíe.',
    'invite.inactive':
      'Este enlace de invitación ya se usó o fue reemplazado por uno más nuevo. Si necesitas acceso, pide a tu administrador que reenvíe la invitación.',
    'invite.unable': 'No se pudo aceptar la invitación.',
    'invite.unreachable': 'No se pudo contactar el servicio de invitaciones. Inténtalo de nuevo.',
    'invite.placeholder': 'Ingresa una nueva contraseña',
    'forgot.unable': 'No se pudo iniciar el restablecimiento de contraseña.',
    'forgot.sent': 'Si existe una cuenta para ese correo, se enviaron instrucciones para restablecer la contraseña.',
    'product.valueTitle': 'Propuesta de valor',
    'terms.versionHistory': '9. Historial de versiones y prueba de reproducción',
    'terms.currentHash': 'Hash de la versión actual:',
    'terms.replay': 'Referencia de reproducción:',
    'cookie.bestPractice': 'Controles de buenas prácticas implementados',
    'cookie.equalChoice': 'Controles de elección igual:',
    'cookie.equalChoiceBody': 'rechazar y aceptar se presentan con la misma prominencia.',
    'cookie.granular': 'Consentimiento granular:',
    'cookie.granularBody': 'las categorías no esenciales se pueden activar de forma independiente.',
    'cookie.noPreEnabled': 'Sin cookies no esenciales preactivadas:',
    'cookie.noPreEnabledBody': 'funcional, analítica y publicidad están desactivadas hasta que las aceptes.',
    'cookie.withdraw': 'Retiro fácil:',
    'cookie.withdrawBody': 'puedes volver a esta página en cualquier momento para cambiar y guardar opciones.',
    'cookie.signal': 'Valores predeterminados sensibles a señales:',
    'cookie.signalBody': 'si se detecta Control de privacidad global, las cookies no esenciales permanecen desactivadas por defecto.',
    'cookie.needRights': '¿Necesitas soporte de derechos de datos además del control de cookies? Usa',
    'cookie.orReview': 'o revisa la',
    'cookie.privacyPolicy': 'Política de Privacidad',
    'cookie.sessionRetention': 'Sesión',
    'cookie.analyticsTablePurpose': 'Apoya medición agregada de uso y ajuste de rendimiento.',
    'cookie.adPurpose': 'Atribuye interacciones de campañas de marketing cuando está habilitado.',
    'cookie.ninetyDays': '90 días',
    'cookie.essentialCat': 'Esencial',
    'cookie.functionalCat': 'Funcional',
    'cookie.analyticsCat': 'Analítica',
    'cookie.advertisingCat': 'Publicidad',
  },
};

export const SPANISH_PUBLIC_ROUTE_HEADINGS: Array<{ path: string; heading: RegExp }> = [
  { path: '/', heading: /Bienvenido a/i },
  { path: '/login', heading: /Iniciar sesión/i },
  { path: '/forgot-password', heading: /Olvidé la contraseña/i },
  { path: '/reset-password', heading: /Elige una nueva contraseña/i },
  { path: '/accept-invite', heading: /Aceptar invitación/i },
  { path: '/legal', heading: /^Legal$/i },
  { path: '/privacy', heading: /Política de Privacidad/i },
  { path: '/terms', heading: /Términos del Servicio/i },
  { path: '/cookie-preferences', heading: /Preferencias de cookies/i },
  { path: '/data-choices', heading: /Tus opciones de datos/i },
  { path: '/trust', heading: /^Confianza$/i },
  { path: '/dmca', heading: /Proceso de avisos DMCA/i },
  { path: '/accessibility', heading: /Declaración de accesibilidad/i },
  { path: '/responsible-disclosure', heading: /Divulgación responsable/i },
  { path: '/claims-evidence', heading: /Registro de evidencia/i },
  { path: '/company', heading: /NextCompli ayuda a equipos regulados/i },
  { path: '/customers', heading: /Cómo los equipos escalan/i },
  { path: '/blog', heading: /Mi blog/i },
  { path: '/news', heading: /Sala de prensa/i },
  { path: '/release', heading: /Notas de la versión/i },
  { path: '/architecture', heading: /ideas, expertos y herramientas/i },
  { path: '/platform', heading: /^Plataforma$/i },
  { path: '/industries', heading: /Innova más rápido/i },
  { path: '/plans', heading: /Productos y precios/i },
  { path: '/dashboards', heading: /Explorar paneles/i },
  { path: '/liability-monitoring', heading: /Monitoreo de responsabilidad/i },
  { path: '/dod-contracts', heading: /Cumplimiento gubernamental/i },
  { path: '/demo-request', heading: /Queremos conocerte mejor/i },
  { path: '/sales-assistant', heading: /Asistente de ventas/i },
  { path: '/signup', heading: /Crea tu cuenta/i },
  { path: '/contact-us', heading: /¿Cómo podemos ayudarte\?/i },
  { path: '/subscribe', heading: /Crea tu cuenta de NextCompli/i },
];
